import React, { useState, useEffect } from 'react';
import { bookings } from '../../api';
import './Bookings.css';

export default function RecurringPatternsList() {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPattern, setSelectedPattern] = useState(null);

  useEffect(() => {
    loadPatterns();
  }, []);

  const loadPatterns = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await bookings.getRecurringPatterns();
      setPatterns(response.data.patterns || []);
    } catch (err) {
      console.error('❌ Error loading patterns:', err);
      setError(err.response?.data?.message || 'Failed to load patterns');
    } finally {
      setLoading(false);
    }
  };

  const handlePauseToggle = async (pattern) => {
    try {
      await bookings.pauseRecurringPattern(pattern._id);
      await loadPatterns();
    } catch (err) {
      console.error('❌ Error toggling pattern:', err);
      setError(err.response?.data?.message || 'Failed to pause pattern');
    }
  };

  const handleDeletePattern = async (patternId) => {
    if (!window.confirm('Delete this pattern and all future slots? This cannot be undone.')) {
      return;
    }

    try {
      await bookings.deleteRecurringPattern(patternId);
      await loadPatterns();
    } catch (err) {
      console.error('❌ Error deleting pattern:', err);
      setError(err.response?.data?.message || 'Failed to delete pattern');
    }
  };

  const getFrequencyLabel = (rrule) => {
    if (!rrule) return '';

    if (rrule.includes('FREQ=DAILY')) return 'Every day';
    if (rrule.includes('FREQ=WEEKLY')) {
      const dayMatch = rrule.match(/BYDAY=([A-Z,]+)/);
      if (dayMatch) {
        const dayMap = {
          'SU': 'Sunday',
          'MO': 'Monday',
          'TU': 'Tuesday',
          'WE': 'Wednesday',
          'TH': 'Thursday',
          'FR': 'Friday',
          'SA': 'Saturday'
        };
        const days = dayMatch[1].split(',').map(d => dayMap[d]).join(', ');
        return `Every week on ${days}`;
      }
      return 'Weekly';
    }
    if (rrule.includes('FREQ=BIWEEKLY')) return 'Every 2 weeks';

    return 'Custom pattern';
  };

  const getNextRunDate = (pattern) => {
    if (!pattern.nextGenerationDate) return null;
    const date = new Date(pattern.nextGenerationDate);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (pattern) => {
    if (pattern.status === 'paused') return '⏸️ Paused';
    if (pattern.status === 'archived') return '📦 Archived';
    return '✅ Active';
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>
        ⏳ Loading patterns...
      </div>
    );
  }

  if (patterns.length === 0) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        color: '#7f8c8d',
        background: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📭</div>
        <div>No recurring patterns yet. Create one to get started!</div>
      </div>
    );
  }

  return (
    <div className="recurring-patterns-list">
      {error && (
        <div className="booking-error" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {patterns.map(pattern => (
        <div key={pattern._id} className="recurring-pattern-card" style={{
          padding: '16px',
          border: '1px solid #e0e6ed',
          borderRadius: '8px',
          marginBottom: '12px',
          background: pattern.status === 'paused' ? '#f8f9fa' : '#fff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#2c3e50' }}>
                {pattern.title}
              </h3>
              <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                {getFrequencyLabel(pattern.rrule)} at {pattern.timeOfDay}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{
                display: 'inline-block',
                padding: '4px 8px',
                background: pattern.status === 'active' ? '#d5f4e6' : '#fff3cd',
                color: pattern.status === 'active' ? '#27ae60' : '#856404',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {getStatusBadge(pattern)}
              </span>
            </div>
          </div>

          <div style={{
            fontSize: '13px',
            color: '#7f8c8d',
            marginBottom: '12px',
            paddingBottom: '12px',
            borderBottom: '1px solid #e0e6ed'
          }}>
            <div>⏱️ {pattern.durationMinutes} minutes per session</div>
            <div>👥 Max {pattern.maxBookings} booking{pattern.maxBookings !== 1 ? 's' : ''}</div>
            <div>🕐 Timezone: {pattern.timezone}</div>
            {pattern.generatedSlotIds && (
              <div>✅ {pattern.generatedSlotIds.length} slots generated</div>
            )}
          </div>

          {pattern.description && (
            <div style={{
              fontSize: '13px',
              color: '#2c3e50',
              marginBottom: '12px',
              paddingBottom: '12px',
              borderBottom: '1px solid #e0e6ed',
              fontStyle: 'italic'
            }}>
              "{pattern.description}"
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => handlePauseToggle(pattern)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '4px',
                border: '1px solid #3498db',
                background: '#ecf0f1',
                color: '#3498db',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {pattern.status === 'active' ? '⏸️ Pause' : '▶️ Resume'}
            </button>
            <button
              onClick={() => handleDeletePattern(pattern._id)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '4px',
                border: '1px solid #e74c3c',
                background: '#fadbd8',
                color: '#e74c3c',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              🗑️ Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
